import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { fadeUp } from "./EasyonMotion";

const companies = ["EasyOn Manufacturing", "EasyOn Retail", "EasyOn Trading"] as const;
type Company = (typeof companies)[number];

const companyData = {
    "EasyOn Manufacturing": { billing: "₹9.2L", inventory: "82%", gst: "On Track" },
    "EasyOn Retail": { billing: "₹12.8L", inventory: "94%", gst: "Filed" },
    "EasyOn Trading": { billing: "₹7.6L", inventory: "76%", gst: "In Review" },
} as const;

export const EasyonHeroSection = () => {
    const navigate = useNavigate();
    const [activeCompany, setActiveCompany] = React.useState<Company>(companies[0]);
    const metrics = companyData[activeCompany];

    return (
        <section className="eo-hero">
            <div className="eo-container eo-hero__inner">
                <motion.div
                    className="eo-hero__copy"
                    initial="hidden"
                    animate="visible"
                    variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
                >
                    <motion.span className="eo-eyebrow" variants={fadeUp}>
                        EasyOn
                    </motion.span>
                    <motion.p className="eo-hero__tagline" variants={fadeUp}>
                        BUSINESS OPERATIONS AUTOMATION PLATFORM
                    </motion.p>
                    <motion.h1 className="eo-hero__title" variants={fadeUp}>
                        Run Your Entire Business{" "}
                        <span className="eo-gradient-text">From One Platform</span>
                    </motion.h1>
                    <motion.p className="eo-hero__desc" variants={fadeUp}>
                        Manage quotations, billing, inventory, GST compliance, vendors, purchases, and
                        operations from a single intelligent workspace.
                    </motion.p>
                    <motion.div className="eo-hero__cta" variants={fadeUp}>
                        <Button className="eo-btn eo-btn--primary" onClick={() => navigate("/contact")}>
                            Request Demo <ArrowRightOutlined />
                        </Button>
                        <Button className="eo-btn eo-btn--ghost" onClick={() => navigate("/contact")}>
                            Get Started
                        </Button>
                    </motion.div>
                </motion.div>

                <div className="eo-hero__showcase">
                    <motion.div
                        className="eo-ops-panel"
                        initial={{ opacity: 0, y: 32, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="eo-ops-panel__header">
                            <strong>Operations Command Center</strong>
                            <span>Multi-Company View</span>
                        </div>
                        <div className="eo-ops-panel__switcher">
                            {companies.map((company) => (
                                <button
                                    key={company}
                                    type="button"
                                    className={company === activeCompany ? "active" : ""}
                                    onClick={() => setActiveCompany(company)}
                                >
                                    {company.replace("EasyOn ", "")}
                                </button>
                            ))}
                        </div>

                        <motion.div
                            key={activeCompany}
                            className="eo-ops-panel__kpis"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35 }}
                        >
                            <div><span>Billing</span><strong>{metrics.billing}</strong></div>
                            <div><span>Inventory</span><strong>{metrics.inventory}</strong></div>
                            <div><span>GST Status</span><strong>{metrics.gst}</strong></div>
                        </motion.div>

                        <div className="eo-ops-panel__lanes">
                            {[
                                { label: "Quotations", width: "78%" },
                                { label: "Purchase Orders", width: "64%" },
                                { label: "Vendor Payments", width: "52%" },
                                { label: "Compliance Queue", width: "84%" },
                            ].map((lane) => (
                                <div key={lane.label} className="eo-ops-panel__lane">
                                    <span>{lane.label}</span>
                                    <div><i style={{ width: lane.width }} /></div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
